import { AnimatePresence, motion } from "framer-motion";
import React, { FC, useMemo, useState } from "react";

import { PaginationArrowButton } from "./pagination-arrow-button";
import { PaginationDots } from "./pagination-dots";
import { PaginationNumbersItem } from "./pagination-numbers-item";

export interface PaginationNumbersProps {
  currentPageIndex: number;
  totalPages: number;
  /**
   * If this function is provided, buttons in the components
   * will be rendered as links and will not trigger setCurrentPageIndex
   * when clicked.
   */
  getItemHref?: (i: number) => string;
  setCurrentPageIndex: (pageIndex: number) => void;
  visibleItemsCount?: number;
}

export const PaginationNumbers: FC<PaginationNumbersProps> = ({
  totalPages,
  currentPageIndex,
  setCurrentPageIndex,
  getItemHref,
  visibleItemsCount = 5,
}) => {
  const visibleItems = useMemo(() => {
    const allPages = new Array(totalPages).fill(null).map((_, i) => i);
    if (totalPages <= visibleItemsCount) return allPages;

    const radius = Math.floor((visibleItemsCount - 1) / 2);
    let startIndex = currentPageIndex - radius;
    let endIndex = currentPageIndex + radius + 1;

    if (startIndex <= 1) {
      // If there are not enough page on the left to be shown, show more page on the right
      // including a space for the ellipsis element
      endIndex += -startIndex + 2;
    }
    if (endIndex + 1 >= totalPages) {
      // If there are not enough page on the right to be shown, show more page on the left,
      // including a space for the ellipsis element
      // unless there are not enough pages at all
      startIndex = Math.max(0, startIndex - (endIndex - totalPages) - 2);
    }

    if (startIndex <= 1) {
      startIndex = 0;
    }
    if (endIndex + 1 > totalPages) {
      endIndex = totalPages;
    }

    return allPages.slice(startIndex, endIndex);
  }, [totalPages, currentPageIndex, visibleItemsCount]);

  const [isOpenDots, setOpenDots] = useState<"left" | "right" | null>(null);

  if (visibleItems.length < 1 || totalPages === 1) return null;

  const hasEllipsisLeft = visibleItems[0] > 1;
  const hasEllipsisRight = visibleItems[visibleItems.length - 1] < totalPages - 2;

  return (
    <ul className="inline-flex gap-1 text-sm">
      <PaginationArrowButton
        direction="left"
        onClick={() => setCurrentPageIndex(currentPageIndex - 1)}
        disabled={currentPageIndex === 0}
      />
      {!visibleItems.includes(0) && (
        <PaginationNumbersItem
          href={getItemHref?.(0)}
          onClick={() => setCurrentPageIndex(0)}
          isActive={currentPageIndex === 0}
        >
          1
        </PaginationNumbersItem>
      )}
      {hasEllipsisLeft && (
        <PaginationDots
          currentPageIndex={currentPageIndex}
          totalPages={totalPages}
          setCurrentPageIndex={setCurrentPageIndex}
          isOpen={isOpenDots === "left"}
          openFn={() => setOpenDots("left")}
          closeFn={() => setOpenDots(null)}
        />
      )}
      <AnimatePresence initial={false} mode="popLayout">
        {visibleItems.map((pageIndex) => (
          <motion.div
            key={pageIndex}
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
            transition={{
              opacity: { ease: "easeOut", duration: 0.2 },
            }}
          >
            <PaginationNumbersItem
              href={getItemHref?.(pageIndex)}
              onClick={() => setCurrentPageIndex(pageIndex)}
              key={pageIndex}
              isActive={pageIndex === currentPageIndex}
            >
              {pageIndex + 1}
            </PaginationNumbersItem>
          </motion.div>
        ))}
      </AnimatePresence>
      {hasEllipsisRight && (
        <PaginationDots
          currentPageIndex={currentPageIndex}
          totalPages={totalPages}
          setCurrentPageIndex={setCurrentPageIndex}
          isOpen={isOpenDots === "right"}
          openFn={() => setOpenDots("right")}
          closeFn={() => setOpenDots(null)}
        />
      )}
      {!visibleItems.includes(totalPages - 1) && (
        <PaginationNumbersItem
          href={getItemHref?.(totalPages - 1)}
          onClick={() => setCurrentPageIndex(totalPages - 1)}
          isActive={currentPageIndex === totalPages - 1}
        >
          {totalPages}
        </PaginationNumbersItem>
      )}
      <PaginationArrowButton
        direction="right"
        onClick={() => setCurrentPageIndex(currentPageIndex + 1)}
        disabled={currentPageIndex === totalPages - 1}
      />
    </ul>
  );
};
